import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDispatch, useSelector } from 'react-redux'
import { filter, find, includes, isEmpty, isNil, join, map, some } from 'lodash'
import { nanoid } from 'nanoid'

import { useQueryParams } from '../../core/hooks'
import { firebase } from '../../core/services'
import { areSameValue } from '../../core/utils'

import {
  ANALYTICS_EVENT_CATEGORIES,
  ANALYTICS_EVENTS,
  COMMON_AVAILABLE_HOTKEYS,
  PAGINATION,
  STATUS_FLAG_FIELD,
} from '../../common/constants'
import { useRowHighlight } from '../../common/hooks'

import { MENU_CURATION_TASK_STATUS } from '../../menu-curation-tasks/constants'
import { DISH_DRAWER_TYPES, DISHES_AVAILABLE_HOTKEYS } from '../constants'
import { updateDishDrawer, updateDishTableHotkeys } from '../redux/actions'

import useUpdateDishesStatusFlagsMutation from './use-update-dishes-status-flags-mutation'

const useDishTableHotkeyHighlights = (isDisabled, taskStatus) => {
  const { buildQueryParams } = useQueryParams()
  const sessionId = useRef()
  const [isNavigationEnabled, setNavigationEnabled] = useState(false)
  const dispatch = useDispatch()

  const {
    isLoading,
    data,
    hotkeys: {
      selectedRowKey,
      selectedCellKey,
      isHotkeyEditModeEnabled,
      isDrawerNavigationEnabled,
    },
    drawer,
  } = useSelector(store => store.dishes.dishes)

  const { result: dishes } = data || {}

  const { pageSize, currentPage } = useQueryParams()

  const areHotkeysDisabled = useMemo(
    () =>
      isHotkeyEditModeEnabled ||
      !isNavigationEnabled ||
      isDisabled ||
      isDrawerNavigationEnabled,
    [
      isDisabled,
      isHotkeyEditModeEnabled,
      isNavigationEnabled,
      isDrawerNavigationEnabled,
    ],
  )

  const isRowSelectionDisabled = useMemo(
    () => !isNavigationEnabled || isDisabled,
    [isDisabled, isNavigationEnabled],
  )

  const isNavigationReadyToBeEnabled = useMemo(
    () => !isDrawerNavigationEnabled && !isDisabled && !isNavigationEnabled,
    [isDisabled, isNavigationEnabled, isDrawerNavigationEnabled],
  )

  const { initHighlight } = useRowHighlight({
    tableSelector: '.dishes-table .ant-table-container table',
    rowsSelector:
      '.dishes-table .ant-table-container table tbody > tr:not(.ant-table-measure-row)',
    cellsSelector: `.dishes-table .ant-table-container table tbody > tr:not(.ant-table-measure-row) td.cell-editable`,
    scrollContainerSelector: '.dishes-table .ant-table-body',
    areHotkeysDisabled,
    isRowSelectionDisabled,
    updateHotkeysAction: updateDishTableHotkeys,
  })

  useEffect(() => {
    setTimeout(initHighlight, 0)
    dispatch(updateDishTableHotkeys({ isHotkeyEditModeEnabled: false }))
  }, [isLoading, initHighlight, pageSize, currentPage, dispatch])

  const handleToggleDrawerVisibility = useCallback(
    drawerType => {
      const shouldClose =
        areSameValue(drawer.selectedDishId, selectedRowKey) &&
        drawer.selectedDrawerType === drawerType

      dispatch(
        updateDishTableHotkeys({ isDrawerNavigationEnabled: !shouldClose }),
      )

      if (shouldClose) {
        return dispatch(
          updateDishDrawer({ selectedDishId: null, selectedDrawerType: null }),
        )
      }

      return dispatch(
        updateDishDrawer({
          selectedDishId: selectedRowKey,
          selectedDrawerType: drawerType,
        }),
      )
    },
    [
      dispatch,
      drawer.selectedDishId,
      drawer.selectedDrawerType,
      selectedRowKey,
    ],
  )

  useHotkeys(
    join(
      [
        DISHES_AVAILABLE_HOTKEYS.OPTION_P.hotkey,
        DISHES_AVAILABLE_HOTKEYS.ALT_P.hotkey,
      ],
      ',',
    ),
    () => {
      handleToggleDrawerVisibility(DISH_DRAWER_TYPES.DISH_DETAILS)

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: join(
            [
              DISHES_AVAILABLE_HOTKEYS.OPTION_P.hotkey,
              DISHES_AVAILABLE_HOTKEYS.ALT_P.hotkey,
            ],
            ',',
          ),
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )
    },
    {
      enabled: !isHotkeyEditModeEnabled && isNavigationEnabled,
    },
    [
      sessionId,
      isNavigationEnabled,
      isHotkeyEditModeEnabled,
      handleToggleDrawerVisibility,
    ],
  )

  useHotkeys(
    join(
      [
        DISHES_AVAILABLE_HOTKEYS.P.hotkey,
        DISHES_AVAILABLE_HOTKEYS.SHIFT_P.hotkey,
      ],
      ',',
    ),
    event => {
      handleToggleDrawerVisibility(
        event.shiftKey
          ? DISH_DRAWER_TYPES.CHOICE_INGREDIENTS
          : DISH_DRAWER_TYPES.MAIN_INGREDIENTS,
      )

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: join(
            [
              DISHES_AVAILABLE_HOTKEYS.P.hotkey,
              DISHES_AVAILABLE_HOTKEYS.SHIFT_P.hotkey,
            ],
            ',',
          ),
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )
    },
    {
      enabled: !isHotkeyEditModeEnabled && isNavigationEnabled,
    },
    [
      sessionId,
      isNavigationEnabled,
      isHotkeyEditModeEnabled,
      handleToggleDrawerVisibility,
    ],
  )

  useHotkeys(
    COMMON_AVAILABLE_HOTKEYS.DOWN.hotkey,
    () => {
      sessionId.current = nanoid()
      setNavigationEnabled(true)
      const [scrollContainer] = document.querySelectorAll(
        '.dishes-table .ant-table-body',
      )
      if (scrollContainer) {
        scrollContainer.scrollIntoView()
      }

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: COMMON_AVAILABLE_HOTKEYS.DOWN.hotkey,
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )
    },
    {
      enabled: isNavigationReadyToBeEnabled,
    },
    [isNavigationReadyToBeEnabled, sessionId],
  )

  const statusFlagFieldName = useMemo(() => {
    if (taskStatus === MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value) {
      return STATUS_FLAG_FIELD.IS_TIER_ONE_DONE
    }

    if (taskStatus === MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value) {
      return STATUS_FLAG_FIELD.IS_CURATION_DONE
    }

    if (taskStatus === MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value) {
      return STATUS_FLAG_FIELD.IS_QA_DONE
    }

    if (taskStatus === MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value) {
      return STATUS_FLAG_FIELD.IS_QC_DONE
    }

    return null
  }, [taskStatus])

  const [updateStatusFlag] =
    useUpdateDishesStatusFlagsMutation(statusFlagFieldName)

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.S.hotkey,
    () => {
      const dish = find(
        dishes,
        ({ id, isLocked }) => !isLocked && id === selectedRowKey,
      )

      if (isEmpty(dish)) {
        return
      }

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: DISHES_AVAILABLE_HOTKEYS.S.hotkey,
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )

      if (taskStatus === MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value) {
        updateStatusFlag([selectedRowKey], !dish.isTierOneDone)
        return
      }

      if (taskStatus === MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value) {
        updateStatusFlag([selectedRowKey], !dish.isCurationDone)
        return
      }

      if (taskStatus === MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value) {
        updateStatusFlag([selectedRowKey], !dish.isQaDone)
        return
      }

      if (taskStatus === MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value) {
        updateStatusFlag([selectedRowKey], !dish.isQcDone)
      }
    },
    {
      enabled:
        !isHotkeyEditModeEnabled &&
        isNavigationEnabled &&
        !isDrawerNavigationEnabled,
    },
    [
      sessionId,
      dishes,
      selectedRowKey,
      isHotkeyEditModeEnabled,
      isNavigationEnabled,
      isDrawerNavigationEnabled,
      taskStatus,
      updateStatusFlag,
    ],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.SHIFT_S.hotkey,
    () => {
      const dishIds = map(
        filter(dishes, dish => !dish.isLocked),
        'id',
      )

      if (
        !includes(
          [
            MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
            MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
          ],
          taskStatus,
        )
      ) {
        return
      }

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: DISHES_AVAILABLE_HOTKEYS.SHIFT_S.hotkey,
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )

      const currentValue = !some(
        filter(dishes, dish => !dish.isLocked),
        dish => {
          if (
            taskStatus === MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value
          ) {
            return !dish.isCurationDone
          }

          if (taskStatus === MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value) {
            return !dish.isCurationDone
          }

          if (taskStatus === MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value) {
            return !dish.isCurationDone
          }

          return null
        },
      )

      if (!isNil(currentValue)) {
        updateStatusFlag(dishIds, !currentValue)
      }
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [sessionId, dishes, areHotkeysDisabled, taskStatus, updateStatusFlag],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ENTER.hotkey,
    event => {
      event.preventDefault()
      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: DISHES_AVAILABLE_HOTKEYS.ENTER.hotkey,
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )

      dispatch(updateDishTableHotkeys({ isHotkeyEditModeEnabled: true }))
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [sessionId, areHotkeysDisabled, selectedRowKey, selectedCellKey],
  )

  useHotkeys(
    join(
      [
        DISHES_AVAILABLE_HOTKEYS.CMD_C.hotkey,
        DISHES_AVAILABLE_HOTKEYS.CTRL_C.hotkey,
      ],
      ', ',
    ),
    async () => {
      const dishPath = buildQueryParams({
        dishIds: [selectedRowKey],
        shouldScrollToDish: true,
        currentPage: PAGINATION.DEFAULT_PAGE,
      })

      firebase.logEvent(
        ANALYTICS_EVENTS.MENU_CURATOR_KEYBOARD_SHORTCUTS
          .MENU_CURATOR_USE_KEYBOARD_SHORTCUTS,
        {
          shortcut: join(
            [
              DISHES_AVAILABLE_HOTKEYS.CMD_C.hotkey,
              DISHES_AVAILABLE_HOTKEYS.CTRL_C.hotkey,
            ],
            ', ',
          ),
          sessionId: sessionId.current,
          eventCategory:
            ANALYTICS_EVENT_CATEGORIES.MENU_CURATOR_KEYBOARD_SHORTCUTS,
        },
      )

      navigator.clipboard.writeText(`${window.location.origin}${dishPath}`)
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [!areHotkeysDisabled, selectedRowKey, sessionId],
  )
}

export default useDishTableHotkeyHighlights
